import { FC } from 'react';
import { TransferRow } from './TransfersPage';
import { useTransfer } from './useTransfer';
import { Button, Flex, message, Table, theme, Typography } from 'antd';
import Column from 'antd/es/table/Column';
import { css } from '@emotion/css';
import { CopyOutlined } from '@ant-design/icons';
import Big from 'big.js';
import { upperCaseFirstLetter } from '../libs/string';

export const DepositInstructions: FC<{ transfer: TransferRow }> = ({ transfer }) => {
  const query = useTransfer(transfer.id, transfer.type);
  const [messageApi, contextHolder] = message.useMessage();
  const { token } = theme.useToken();
  const dataSource = [];

  if (query.data?.onramp_transfer && Object.keys(query.data?.onramp_transfer).length) {
    dataSource.push(
      {
        key: 'Payment rail',
        value: query.data.onramp_transfer.quote.deposit_rails.toUpperCase(),
      },
      {
        key: 'Account holder name',
        value: query.data.onramp_transfer.quote.deposit_bank_account.account_beneficiary_name,
      },
      {
        key: 'Bank name',
        value: query.data.onramp_transfer.quote.deposit_bank_account.bank_name,
      }
    );

    if (query.data?.onramp_transfer.quote.deposit_bank_account.iban) {
      dataSource.push(
        {
          key: 'IBAN',
          value: query.data?.onramp_transfer.quote.deposit_bank_account.iban.iban_number,
        },
        {
          key: 'BIC',
          value: query.data?.onramp_transfer.quote.deposit_bank_account.iban.bic,
        }
      );
    } else if (query.data?.onramp_transfer.quote.deposit_bank_account.us) {
      dataSource.push(
        {
          key: 'Account Number',
          value: query.data?.onramp_transfer.quote.deposit_bank_account.us.account_number,
        },
        {
          key: 'Routing Number',
          value: query.data?.onramp_transfer.quote.deposit_bank_account.us.routing_number,
        }
      );
    }

    dataSource.push(
      {
        key: 'Currency',
        value: query.data.onramp_transfer.quote.deposit_currency.toUpperCase(),
      },
      {
        key: 'Deposit message',
        value: query.data.onramp_transfer.quote.deposit_message,
      },
      {
        key: 'Deposit amount',
        value: new Big(query.data.onramp_transfer?.quote?.deposit_amount).toFixed(2),
      },
    );
  } else if (query.data?.offramp_transfer && Object.keys(query.data?.offramp_transfer).length) {
    dataSource.push(
      {
        key: 'Blockhain',
        value: upperCaseFirstLetter(query.data.offramp_transfer?.quote?.deposit_network),
      },
      {
        key: 'Deposit address',
        value: query.data.offramp_transfer?.quote?.deposit_blockchain_address,
      },
      {
        key: 'Deposit currency',
        value: query.data.offramp_transfer?.quote?.deposit_token?.toUpperCase(),
      },
      {
        key: 'Deposit amount',
        value: new Big(query.data.offramp_transfer?.quote?.deposit_amount || '0').toFixed(2),
      },
    );
  }

  if (dataSource.length === 0 || query.isPending) {
    return null;
  }

  return (
    <>
      {contextHolder}

      <Table
        loading={query.isPending || query.isFetching}
        title={() => {
          return (
            <Typography.Title style={{ margin: 0 }} level={5}>
              Deposit Instructions
            </Typography.Title>
          );
        }}
        size="small"
        showHeader={false}
        rowHoverable={false}
        pagination={false}
        dataSource={dataSource}
      >
        <Column
          key="key"
          width={250}
          render={(v) => {
            return v.key;
          }}
        />

        <Column
          key="value"
          render={(v) => {
            const subHeaderStyles = css`
              color: ${token.colorTextTertiary};
            `;
            if (
              ['IBAN', 'BIC', 'Account number', 'Deposit address', 'Deposit amount', 'Routing number', 'Account holder', 'Deposit message'].includes(v.key)
            ) {
              return (
                <Flex align="center" gap={'small'}>
                  <span style={{ wordBreak: 'break-all' }}>{v.value}</span>

                  <Button
                    type="text"
                    className={subHeaderStyles}
                    size="small"
                    icon={<CopyOutlined width={16} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(v.value);
                      messageApi.success('Copied to clipboard');
                    }}
                  />
                </Flex>
              );
            }

            return v.value;
          }}
        />
      </Table>
    </>
  );
};
